import React from 'react';

import PageSection from '../../components/PageSection';
import DataTable from '../../components/DataTable';

import { useAPI } from '../../api';
import { Route, Routes } from 'react-router-dom';
import AuthStrategyContainer from './AuthStrategyContainer';
import RawJsonDisplay from '../../components/RawJsonDisplay';
import { AuthStrategyItem } from 'types';
import { OrgContextLink, useOrgContext } from 'components/OrgContextProvider';

const AuthSettingsContainer = () => {
	const api = useAPI();
	const ctx = useOrgContext();

	const [loading, setLoading] = React.useState(true);

	const [authStrategies, setAuthStrategies] = React.useState<AuthStrategyItem[]>([
		{ id: '', strategy: 'local', enabled: false, settings: {}, knownSecrets: [] },
		{ id: '', strategy: 'ldap', enabled: false, settings: {}, knownSecrets: [] },
		{ id: '', strategy: 'saml', enabled: false, settings: {}, knownSecrets: [] },
		{ id: '', strategy: 'google', enabled: false, settings: {}, knownSecrets: [] }
	]);

	const loadAuthStrategies = async () => {
		setLoading(true);

		const local = await api.get<AuthStrategyItem>(`${ctx?.apiBaseSettings}/auth/local`);
		const ldap = await api.get<AuthStrategyItem>(`${ctx?.apiBaseSettings}/auth/ldap`);
		const saml = await api.get<AuthStrategyItem>(`${ctx?.apiBaseSettings}/auth/saml`);
		const google = await api.get<AuthStrategyItem>(`${ctx?.apiBaseSettings}/auth/google`);

		setAuthStrategies([
			local,
			ldap,
			saml,
			google
		]);

		setLoading(false);
	};

	React.useEffect(() => {
		loadAuthStrategies();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	function deleteAuthStrategy(strategy: string) {
		if (window.confirm(`Delete strategy ${strategy}?`)) {
			api.delete(`${ctx?.apiBaseSettings}/auth/${strategy}`)
			.then(loadAuthStrategies);
		}
	};

	return (
		<div>
			<PageSection title="Authentication Settings" level={2} loading={loading} borderEnd={false}>
				<Routes>
					<Route path="/" element={
						<div>
							<PageSection title="Auth Strategies" level={3}>
								<DataTable<AuthStrategyItem>
									data={authStrategies}
									columns={[
										{ label: 'Strategy', key: 'strategy', render: (item) => (<OrgContextLink to={`/settings/auth/${item.strategy}`}>{item.strategy}</OrgContextLink>) },
										{ label: 'Enabled', key: 'enabled', render: (item) => item.enabled ? 'Yes' : 'No' },
										{
											label: 'Settings',
											key: 'settings',
											render: (row) => {
												const settings = row.settings;
												const knownSecrets = row.knownSecrets;
												return (
													<code>
														<div>
															<div>
																{
																	settings && Object.keys(settings).length > 0
																	? (Object.keys(settings).map(key => (<div key={key}><b>{key}</b>: {String(settings[key]).length > 80 ? `${String(settings[key]).substring(0, 80)}...` : String(settings[key])}</div>)))
																	: 'No settings'
																}
															</div>

															<div>
																{
																	knownSecrets && knownSecrets.length > 0
																	? (knownSecrets.map(key => (<div style={{ fontStyle: 'italic', color: '#80f080' }} key={key}><b>{key}</b>: ********</div>)))
																	: 'No secrets'
																}
															</div>
														</div>
													</code>
												);
											}
										}
									]}
									tools={[
										(item) => (<button className="link" onClick={() => deleteAuthStrategy(item.strategy)}>Delete</button>)
									]}
								/>
							</PageSection>

							<PageSection title="Raw Data" level={3}>
								<RawJsonDisplay data={authStrategies} />
							</PageSection>
						</div>
					} />

					<Route path="/:authStrategyID" element={<AuthStrategyContainer onChanged={loadAuthStrategies} />} />
				</Routes>
			</PageSection>
		</div>
	);
};

export default AuthSettingsContainer;
